import React, { useState } from 'react';
import { CalculatorIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import quoteService from '../../services/quoteService';
import QuoteEditor, { QUOTE_STATUS } from './QuoteEditor';
import { Issue, rands } from '../billing/TariffLines';

const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' }) : '');

/**
 * AssistantQuote — a quote the assistant drafted ("Quote case …"), in the
 * conversation: the estimate and the codes it chose. "Review & send" opens the
 * full quote editor to change codes or time and send it to the patient.
 *   quote: the saved quote   estimate: its priced lines and issues (with the assistant's notes)
 */
const AssistantQuote = ({ quote: initial, estimate }) => {
  const [quote, setQuote] = useState(initial);
  const [open, setOpen] = useState(false);
  const s = QUOTE_STATUS[quote.status] || { label: quote.status, tone: 'bg-slate-100 text-slate-600' };
  const lines = (estimate?.practice?.lines || quote.practice_lines || []).filter((l) => l.amount != null);
  const issues = quote.status === 'draft' ? estimate?.issues || [] : [];

  const refresh = async () => {
    try { setQuote(await quoteService.get(quote.quote_id)); } catch { /* keep what we have */ }
  };

  return (
    <div className="overflow-hidden rounded-xl border-2 border-teal-200 bg-white text-sm shadow-sm">
      <div className="flex items-start gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
        <CalculatorIcon className="mt-0.5 h-5 w-5 text-brand-teal" />
        <div className="min-w-0 flex-1">
          <div className="font-bold text-brand-navy">Quote {quote.quote_no} · {quote.patient_name}</div>
          <div className="truncate text-xs text-slate-500">{[fmtDate(quote.service_date), quote.hospital_name, quote.surgeon, quote.procedure_text].filter(Boolean).join(' · ')}</div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${s.tone}`}>{s.label}</span>
      </div>

      <div className="grid grid-cols-3 gap-2 px-4 py-3 text-center">
        <div className="rounded-lg bg-slate-50 px-2 py-2">
          <div className="text-[11px] text-slate-500">Anaesthetic fee</div>
          <div className="font-bold text-slate-800">{rands(quote.practice_total)}</div>
        </div>
        <div className="rounded-lg bg-slate-50 px-2 py-2">
          <div className="truncate text-[11px] text-slate-500">{quote.payer === 'private' ? 'Medical aid' : `${quote.scheme_name || 'Medical aid'} pays`}</div>
          <div className="font-bold text-slate-800">{quote.payer === 'private' ? '—' : quote.scheme_total == null ? '?' : rands(quote.scheme_total)}</div>
        </div>
        <div className="rounded-lg bg-brand-deep px-2 py-2 text-white">
          <div className="text-[11px] text-teal-100">Patient pays</div>
          <div className="font-bold">{quote.shortfall == null ? '?' : rands(quote.shortfall)}</div>
        </div>
      </div>

      <ul className="divide-y divide-slate-100 border-t border-slate-100">
        {lines.map((l, i) => (
          <li key={`${l.code}-${i}`} className="flex gap-3 px-4 py-1.5 text-xs">
            <span className="w-10 shrink-0 font-mono font-semibold text-brand-navy">{l.code}</span>
            <span className="flex-1 text-slate-700">{l.description}</span>
            <span className="tabular-nums text-slate-700">{rands(l.amount)}</span>
          </li>
        ))}
      </ul>
      <p className="px-4 pt-1.5 text-[11px] text-slate-400">
        {quote.facts?.duration_minutes != null && `Expected anaesthetic time ${quote.facts.duration_minutes} min · `}an estimate on placeholder rates
      </p>
      {issues.length > 0 && <div className="space-y-1 px-4 pt-2">{issues.map((i) => <Issue key={i.message} issue={i} />)}</div>}

      <div className="flex justify-end px-4 py-3">
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-3 py-2 text-sm font-semibold text-white hover:opacity-95">
          <PaperAirplaneIcon className="h-4 w-4" /> {quote.status === 'draft' ? 'Review & send' : 'Open quote'}
        </button>
      </div>

      {open && (
        <QuoteEditor
          bid={quote.bid}
          patient={{ patient_id: quote.patient_id, name: quote.patient_name, email: quote.patient_email, payer: quote.payer, scheme_name: quote.scheme_name }}
          bookingLabel={[fmtDate(quote.service_date), quote.hospital_name, quote.surgeon].filter(Boolean).join(' · ')}
          plannedProcedure={quote.procedure_text}
          onClose={() => { setOpen(false); refresh(); }}
          onChanged={refresh}
        />
      )}
    </div>
  );
};

export default AssistantQuote;
